import type { FC } from "react";
import React from "react";
import { i18nFilter } from "app/filters/i18nFilter";
import { DefaultCellHeader } from "app/components/React/Table/headerCells";
import { TrafficShare } from "app/components/React/Table/cells/TrafficShare";
import { SecondaryBoxTitle } from "pro-features/styled components/StyledBoxTitle/src/StyledBoxTitle";
import { LinkCell } from "pro-features/components/Workspace/TableCells/LinkCell";
import {
    StyledBox,
    StyledCellCategory,
    StyledTable,
    TitleWrapper,
} from "app/pages/website-analysis/analyze-publishers/advertisers/common/StyledComponents";
import type { IFilterItem } from "app/pages/website-analysis/analyze-publishers/advertisers/common/types";
import { NoDataLandscape } from "pro-features/components/NoData/src/NoData";

const AdvertisersTopIndustriesDistributionInner: FC<{
    data: IFilterItem[];
    getLink: (key: string, value: string) => string;
    onClick: (category: string) => void;
}> = ({ data, getLink, onClick }) => {
    const i18n = i18nFilter();
    const title = i18n("analysis.publishers.advertisers.top.categories.title");
    const tooltip = i18n("analysis.publishers.advertisers.top.categories.info");

    const transformData = () => {
        const getItem = (item) => {
            const categoryChild = item.Name.substring(item.Name.indexOf("/") + 1);
            const category = categoryChild ? categoryChild : item.Name;
            return {
                Category: category.replace(/_/g, " "),
                Share: item.Value,
                CategoryApi: item.Name.replace("/", "~"),
            };
        };
        const topFive = [];
        data.slice(0, 5).forEach((item) => {
            if (item.Name !== "Unknown") {
                topFive.push(getItem(item));
            }
        });

        if (data.length > 5) {
            if (topFive.length < 5) {
                topFive.push(getItem(data[5]));
            }

            const sum = topFive.reduce((acc: number, val) => {
                acc += val.Share;
                return acc;
            }, 0);

            return [...topFive, { Category: `Others (${data.length - 5})`, Share: 1 - sum, CategoryApi: null }];
        } else {
            return topFive;
        }
    };

    const getTableColumns = () => {
        return [
            {
                field: "Category",
                displayName: i18n("analysis.publishers.advertisers.top.categories.category.column.title"),
                headerComponent: DefaultCellHeader,
                cellComponent: (props) => {
                    const { value, row } = props;
                    return row.CategoryApi ? (
                        <StyledCellCategory>
                            <LinkCell
                                href={getLink("industry", row.CategoryApi)}
                                value={value}
                                title={value}
                                onClick={() => onClick(row.CategoryApi)}
                            />
                        </StyledCellCategory>
                    ) : (
                        <div>{value}</div>
                    );
                },
            },
            {
                field: "Share",
                displayName: i18n("analysis.publishers.advertisers.top.categories.share.column.title"),
                headerComponent: DefaultCellHeader,
                cellComponent: TrafficShare,
            },
        ];
    };

    const getComponent = () =>
        data && data.length > 0 ? (
            <StyledTable
                className="MiniFlexTable"
                data={transformData()}
                columns={getTableColumns()}
            />
        ) : (
            <NoDataLandscape
                title="analysis.publishers.advertisers.empty.state.title"
                subtitle=""
            />
        );

    return (
        <StyledBox>
            <TitleWrapper>
                <SecondaryBoxTitle tooltip={tooltip}>{title}</SecondaryBoxTitle>
            </TitleWrapper>
            {getComponent()}
        </StyledBox>
    );
};

const propsAreEqual = (prevProps, nextProps) => {
    const { data } = prevProps;
    const { data: nextData } = nextProps;
    return data === nextData;
};

export const AdvertisersTopIndustriesDistribution = React.memo(
    AdvertisersTopIndustriesDistributionInner,
    propsAreEqual,
);
